import React from 'react'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardActions from '@material-ui/core/CardActions'
import CardContent from '@material-ui/core/CardContent'
import CardMedia from '@material-ui/core/CardMedia'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import { makeStyles } from '@material-ui/core/styles'
import { gtmEvent } from 'lib/gtm'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  PROMO_CALLOUT_BORDER_RADIUS,
} from 'lib/form-field-styles'
import { useLoadGroupEventFormValues } from 'components/group-events/create-group-event-form-wizard/hooks'
import { fullDayDateAndTime } from 'utils/dateUtils'
import { composeOptimizedImageUrl } from 'lib/utils'
import { interactiveCardHoverStyles } from 'lib/styles/interactive-card-hover'

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    maxHeight: '600px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    cursor: 'pointer',
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    border: '1px solid #E0E0E0',
    boxShadow: 'none',
    overflow: 'hidden',
    '&.MuiPaper-root': {
      borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
    },
    ...interactiveCardHoverStyles(theme),
  },
  cardMedia: {
    height: 200,
    borderTopLeftRadius: PROMO_CALLOUT_BORDER_RADIUS,
    borderTopRightRadius: PROMO_CALLOUT_BORDER_RADIUS,
  },
  cardActions: {
    justifyContent: 'center',
    padding: '0 0 20px',
  },
  caption: {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
  },
  tonalButton: {
    color: CALLOUT_FOREGROUND,
    backgroundColor: CALLOUT_BACKGROUND,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    padding: '8px 10px',
    '&:hover': {
      backgroundColor: CALLOUT_BACKGROUND,
      boxShadow: 'none',
    },
  },
  tagRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagChip: {
    backgroundColor: CALLOUT_BACKGROUND,
    color: CALLOUT_FOREGROUND,
    border: 'none',
    fontWeight: 500,
  },
}))

const GroupEventTemplateCard = ({
  event,
  callToAction,
  onCreateClick,
  isTemplate = false,
}) => {
  const classes = useStyles()
  const loadGroupEventValues = useLoadGroupEventFormValues()
  const selectTemplate = () => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'group-event-templates',
      formAction: event.title
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-'),
    })
    loadGroupEventValues({
      ...event,
      template: isTemplate
        ? { id: event.id, slug: event.slug }
        : event.template || null,
    })
    onCreateClick && onCreateClick()
  }
  return (
    <Card className={classes.card} onClick={selectTemplate}>
      <Box style={{ display: 'flex', flexDirection: 'column' }}>
        <CardMedia
          className={classes.cardMedia}
          image={composeOptimizedImageUrl(event.featuredImageUrl, 600)}
          title={event.title}
        >
          {event.isNew ? (
            <Box style={{ position: 'relative', left: '16px', top: '16px' }}>
              <Chip
                label={<Typography variant="caption">NEW</Typography>}
                color="secondary"
              />
            </Box>
          ) : null}
        </CardMedia>
        <CardContent style={{ display: 'flex', flexDirection: 'column' }}>
          <Box mb={2}>
            <Box mb={1}>
              <Typography variant="h6">{event.title}</Typography>
            </Box>
            {event.caption != null && (
              <Typography
                display="block"
                variant="body2"
                color="textSecondary"
                className={classes.caption}
              >
                {event.caption}
              </Typography>
            )}
            {event.coachFacility && (
              <Typography display="block" variant="caption">
                {event.coachFacility.name}
              </Typography>
            )}
            {event.startDateTime && event.endDateTime && (
              <Typography variant="caption">
                {fullDayDateAndTime(event.startDateTime, event.endDateTime)}
              </Typography>
            )}
            {event.priceInCents != null && (
              <Typography display="block" variant="caption">
                ${(event.priceInCents / 100).toFixed(2)}
              </Typography>
            )}
          </Box>
          <Box className={classes.tagRow}>
            {event.tags.map(tag => (
              <Chip
                key={tag}
                size="small"
                label={tag}
                className={classes.tagChip}
              />
            ))}
          </Box>
        </CardContent>
      </Box>
      <CardActions className={classes.cardActions}>
        <Button
          variant="contained"
          className={classes.tonalButton}
          onClick={selectTemplate}
          data-cy="use-template"
        >
          {callToAction}
        </Button>
      </CardActions>
    </Card>
  )
}

export default GroupEventTemplateCard
